import { memoryType } from "_src/engine/constants";
import output from "_src/engine/output";
import app_state from "_src/app_state";
import renderGraph from "_src/render_graph/render_graph";
import events from "_src/engine/events";

class Statistic {
  constructor() {
    this.gpu = {
      geometry: {count: 0, size: 0},
      textures: {count: 0, size: 0},
      fbo: {count: 0, size: 0},
    };

    this.cpu = {
      geometry: {count: 0, size: 0},
    };

    this.registeredObjects = new WeakMap();
  }

  getReport() {

    let total = this.cpu.geometry.count + this.gpu.geometry.count + this.gpu.textures.count + this.gpu.fbo.count;

    if (total == 0) {
      return null;
    }

    let report = {};

    report["Cpu geometry count"] = {
      count  : this.cpu.geometry.count,
      memory : getReadableMemoryStr(this.cpu.geometry.size)
    };

    report["Gpu geometry count"] = {
      count  : this.gpu.geometry.count,
      memory : getReadableMemoryStr(this.gpu.geometry.size)
    };

    report["Gpu textures count"] = {
      count  : this.gpu.textures.count,
      memory : getReadableMemoryStr(this.gpu.textures.size)
    };

    report["Gpu framebuffers attachments count"] = {
      count  : this.gpu.fbo.count,
      memory : getReadableMemoryStr(this.gpu.fbo.size)
    };

    report["Gpu objects count"] = {
      count  : this.gpu.geometry.count + this.gpu.textures.count + this.gpu.fbo.count,
      memory : getReadableMemoryStr(this.gpu.geometry.size + this.gpu.textures.size + this.gpu.fbo.size)
    };

    return report;
  }
}

const REPORT_INTERVAL = 0.25;

let _lastReportTime = 0;
let _currentRenderGraphId = 0;

const _renderGraphsId = new WeakMap();
const _scenes = new WeakMap();
const _textureIsAttachment = new WeakSet();
const _objectToStatistics = new WeakMap();

const _reportMemUsage = true;
const _reportBandwidth = false;

const _globalStat = new Statistic();
const _detailedStat = [];

export default {

  registerScene(scene, renderGraph, passId) {
    let renderGraphId = _renderGraphsId.get(renderGraph);
    if (renderGraphId === undefined) {
      renderGraphId = _currentRenderGraphId++;
      _renderGraphsId.set(renderGraph, renderGraphId);
    }

    _scenes.set(scene, {renderGraphId, passId});

    let renderGraphStat = _detailedStat[renderGraphId];
    if (renderGraphStat === undefined) {
      renderGraphStat = [];
      _detailedStat[renderGraphId] = renderGraphStat;
    }

    let passStat = renderGraphStat[passId];
    if (passStat === undefined) {
      passStat = new Statistic();
      renderGraphStat[passId] = passStat;
    }
  },

  markMemoryAllocated(object, type, size, scene) {
    const sceneEntry = _scenes.get(scene);
    const newStatistic = sceneEntry !== undefined ? _detailedStat[sceneEntry.renderGraphId][sceneEntry.passId] : _globalStat;

    //process changes of this object in other statistics
    let objectStatisticsSet = _objectToStatistics.get(object);
    if (!objectStatisticsSet) {
      objectStatisticsSet = new Set();
      _objectToStatistics.set(object, objectStatisticsSet);
    }
    objectStatisticsSet.add(newStatistic);

    //texture can turn into fbo attachment but not in opposite way
    if (type === memoryType.GPU_TEXTURES && _textureIsAttachment.has(object)) {
      type = memoryType.GPU_FRAMEBUFFERS;
    } else if (type === memoryType.GPU_FRAMEBUFFERS) {
      _textureIsAttachment.add(object);
    }

    for (const statistic of objectStatisticsSet) {
      const previousAllocation = statistic.registeredObjects.get(object);
      if (previousAllocation) {
        if (previousAllocation.type === type && previousAllocation.size === size) {
          continue;
        }

        this._markMemoryAllocation(previousAllocation.type, -previousAllocation.size, statistic);
      }

      statistic.registeredObjects.set(object, {type, size});

      this._markMemoryAllocation(type, size, statistic);
      if (_reportMemUsage) {
        let allocationString;
        if (sceneEntry) {
          allocationString = constructAllocationString(true, size, type, sceneEntry.renderGraphId, sceneEntry.passId);
        } else {
          allocationString = constructAllocationString(true, size, type);
        }
        const usageInfo = constructUsageTable();
        events.emit("memory_report", { memory : allocationString, passes : usageInfo });
      }

    }
  },

  markMemoryReleased(object, scene) {
    const sceneEntry = _scenes.get(scene);
    const statistic = sceneEntry !== undefined ? _detailedStat[sceneEntry.renderGraphId][sceneEntry.passId] : _globalStat;

    const allocation = statistic.registeredObjects.get(object);
    if (!allocation) {
      return;
    }

    statistic.registeredObjects.delete(object);
    const objectStatisticsSet = _objectToStatistics.get(object);
    if (!objectStatisticsSet) {
      output.error(output.DEVELOPERS_ERROR, "No object's statistic when deleting it");
      return;
    }
    objectStatisticsSet.delete(statistic);

    this._markMemoryAllocation(allocation.type, -allocation.size, statistic);
    if (_reportMemUsage) {
      let releaseString;
      if (sceneEntry) {
        releaseString = constructAllocationString(false, allocation.size, allocation.type, sceneEntry.renderGraphId, sceneEntry.passId);
      } else {
        releaseString = constructAllocationString(false, allocation.size, allocation.type);
      }
      const usageString = constructUsageTable();
      events.emit("memory_report", { memory : releaseString, passes : usageString });
    }
  },

  update(time) {

    const memoryUsage = _globalStat.cpu.geometry.size;
    const dt = time - _lastReportTime;
    const lastReportedMemory = app_state.getStateValue("memoryUsage");

    if (dt > REPORT_INTERVAL && lastReportedMemory !== memoryUsage) {

      if (_reportBandwidth) {
        const bandwidth = (memoryUsage - lastReportedMemory) / dt ;

        output.log("Bandwidth:" + getReadableMemoryStr(bandwidth) + "/s");

      }

      _lastReportTime = time;

      app_state.setStateValue("memoryUsage", memoryUsage);

    }

  },

  _markMemoryAllocation(type, size, statistic) {

    switch(type) {
      case memoryType.CPU_GEOMETRY:
        statistic.cpu.geometry.size += size;
        statistic.cpu.geometry.count += Math.sign(size);
        break;
      case memoryType.GPU_GEOMETRY:
        statistic.gpu.geometry.size += size;
        statistic.gpu.geometry.count += Math.sign(size);
        break;
      case memoryType.GPU_TEXTURES:
        statistic.gpu.textures.size += size;
        statistic.gpu.textures.count += Math.sign(size);
        break;
      case memoryType.GPU_FRAMEBUFFERS:
        statistic.gpu.fbo.size += size;
        statistic.gpu.fbo.count += Math.sign(size);
        break;
    }

  },

  getReadableMemoryStr

}

function constructAllocationString(isAllocation, size, type, graphId, passId) {
  let allocationString = isAllocation ? "Allocated " : "Released ";
  allocationString += size + " bytes for ";
  switch (type) {
    case memoryType.CPU_GEOMETRY:
      allocationString += "cpu geometry";
      break;
    case memoryType.GPU_GEOMETRY:
      allocationString += "gpu geometry";
      break;
    case memoryType.GPU_TEXTURES:
      allocationString += "gpu textures";
      break;
    case memoryType.GPU_FRAMEBUFFERS:
      allocationString += "gpu framebuffers";
      break;
  }
  if (graphId !== undefined && passId !== undefined) {
    allocationString += " in renderGraph " + graphId + ", pass " + passId;
  }
  allocationString += ".\n";

  return allocationString;
}

function constructUsageTable() {
  let usageInfo = {
    renderGraphs : [],
    total        : ""
  };
  for (let i = 0; i < _detailedStat.length; i++){
    let renderGraphInfo = null;
    for (let j = 0; j < _detailedStat[i].length; j++){
      const passInfo = _detailedStat[i][j].getReport();
      if (passInfo) {
        if (!renderGraphInfo) {
          renderGraphInfo = {};
        }
        renderGraphInfo[renderGraph.getPassName(j)] = passInfo;
      }
    }
    if (renderGraphInfo) { 
      usageInfo.renderGraphs.push(renderGraphInfo);
    }
  }

  usageInfo.total = _globalStat;

  return usageInfo;
}

function getReadableMemoryStr(memInBytes) {
  return memInBytes > 1000000000 ? (memInBytes / 1000000000).toFixed(1) + " Gb" :
         memInBytes > 1000000    ? (memInBytes / 1000000).toFixed(1) + " Mb" :
         memInBytes > 1000       ? (memInBytes / 1000).toFixed(1) + " Kb":
                                    memInBytes + " B";
}
